import { TextAlignLeftIcon } from '@phosphor-icons/react';
import type * as React from 'react';
import { useMemo, useState } from 'react';

import { ConceptCopySection } from './ConceptCopySection';
import { ConceptCritiqueSection, type ConceptCritiqueSectionProps } from './ConceptCritiqueSection';

import { Textarea } from '@/components/ui/textarea';
import {
  CollapsibleSection,
  NumericPairRow,
  PropertyRow,
  type RatioVariant,
  RatioVariantStrip,
  type TextStyleState,
  TextStyleToggles,
  WorkbenchAccordion,
} from '@/components/workbench';
import {
  fillAngle,
  fillStops,
  fillTypeLabel,
  isGradientFill,
  withFillAngle,
  withFillStop,
} from '@/lib/concept-workbench/fill';
import { fontScaleFromSize, materialiseFontSize } from '@/lib/concept-workbench/stage-dimensions';
import type {
  AspectRatioToken,
  LayerOverride,
  OverlayFill,
  SetLayer,
  SetLogoLayer,
  SetShapeLayer,
  SetTextLayer,
  VariantLayerGeometry,
  VariantState,
} from '@/lib/concept-workbench/types';
import { OVERLAY_FONT_FAMILIES } from '@/lib/fonts/load-google-font';
import type {
  Placement as MatrixPlacement,
  Platform as MatrixPlatform,
} from '@/lib/platform-matrix/placement-matrix';
import { cn } from '@/lib/utils';

/**
 * The right inspector, in the order the reference (Hightouch, measured —
 * `ht-editor-shell.png` / `ht-right-inspector.png`) actually proves works:
 *
 *   Size variants → Text → Typography → Position → Dimensions → Concept → Content
 *
 * This ORDER CHANGED 2026-08-15 (`docs/planning/HANDOFF-EDITOR-UNIFICATION-
 * 2026-08-12.md` §8.7/§8.14 P1). It used to put `Concept`/`Content` above the
 * selected layer's own properties, which measured as `SIZE VARIANTS` (y=59) →
 * `CONCEPT` (y=196) → `CONTENT` (y=503) → `TEXT` (y=857, below the fold) — the
 * properties of the thing the user just clicked were the LAST thing in the
 * rail, under ~800px of static concept metadata and copy governance. The
 * layer section is now the SECOND thing in the column, directly under the
 * ratio strip, matching Hightouch's `Text`/`Typography`/`Position`/
 * `Dimensions` groups sitting right under its size-variant strip.
 *
 * `Concept`/`Content` still sit BELOW the layer section rather than being
 * removed — they are real, reviewed governance data, just lower-priority than
 * "style the thing I clicked" the instant a layer is selected. WITH NO LAYER
 * SELECTED the layer sections are REMOVED FROM THE DOM, not disabled, and the
 * column falls back to a one-line hint above `Concept`/`Content` — a column of
 * four greyed-out accordions would be noise pretending to be information.
 *
 * The collapsible-group PATTERN itself is untouched — same `CollapsibleSection`
 * primitive, same behaviour, same styling. Only the order changed, plus this
 * section now mounts open by default the instant a layer is selected (it did
 * before too, via `defaultOpen`; this comment states it explicitly because a
 * reader coming from the old order needs to know intent didn't change there).
 */

const FONT_WEIGHTS = [
  { value: '100', label: 'Thin' },
  { value: '300', label: 'Light' },
  { value: '400', label: 'Regular' },
  { value: '500', label: 'Medium' },
  { value: '600', label: 'SemiBold' },
  { value: '700', label: 'Bold' },
  { value: '800', label: 'ExtraBold' },
  { value: '900', label: 'Black' },
];

const ALIGN_OPTIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Centre' },
  { value: 'right', label: 'Right' },
];

const VERTICAL_ALIGN_OPTIONS = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
];

const SHAPE_TYPES = [
  { value: 'rectangle', label: 'Rectangle' },
  { value: 'circle', label: 'Circle' },
  { value: 'ellipse', label: 'Ellipse' },
  { value: 'star', label: 'Star' },
  { value: 'triangle', label: 'Triangle' },
  { value: 'line', label: 'Line' },
];

const LOGO_VARIANTS = [
  { value: 'primary', label: 'Primary' },
  { value: 'light', label: 'Light' },
  { value: 'dark', label: 'Dark' },
];

/**
 * THE FILL ROWS — the fix for a real data-loss bug.
 *
 * The old `resolveFillHex` flattened a gradient to `colors[0]` for display, and
 * the colour row wrote a bare hex back, so touching the swatch on a
 * gradient-filled layer destroyed the second stop and the angle silently. A
 * lossy read is only tolerable if nothing writes; this wrote.
 *
 * So a gradient is now shown as what it is — a type readout, one row per stop,
 * and an angle — and every write goes through `withFillStop` / `withFillAngle`,
 * which return a fill of the SAME shape. A solid fill still renders exactly one
 * `Colour` row, so the common case did not grow.
 */
function FillRows({
  fill,
  disabled,
  onChange,
}: {
  fill: OverlayFill;
  disabled?: boolean;
  onChange: (next: OverlayFill) => void;
}): JSX.Element {
  const stops = fillStops(fill);
  const gradient = isGradientFill(fill);

  return (
    <>
      {gradient ? (
        <PropertyRow variant="readonly" label="Fill" value={fillTypeLabel(fill)} />
      ) : null}
      {stops.map((hex, index) => (
        <PropertyRow
          key={index}
          variant="color"
          disabled={disabled}
          label={gradient ? `Stop ${index + 1}` : 'Colour'}
          value={hex}
          onChange={(next) => onChange(withFillStop(fill, index, next))}
        />
      ))}
      {gradient ? (
        <PropertyRow
          variant="input"
          label="Angle"
          disabled={disabled}
          unit="°"
          inputMode="numeric"
          value={String(fillAngle(fill))}
          onChange={() => {}}
          onCommit={(value) => {
            const next = Number(value);
            if (!Number.isFinite(next)) return;
            onChange(withFillAngle(fill, next));
          }}
        />
      ) : null}
    </>
  );
}

export interface WorkbenchInspectorProps {
  variants: readonly RatioVariant[];
  selectedRatio: AspectRatioToken;
  onSelectRatio: (ratio: AspectRatioToken) => void;
  onAddRatio: (ratio: AspectRatioToken) => void;
  /** Set-level size actions (regenerate) shown in the Size variants header, never in the prompt scroller. */
  sizeActions?: React.ReactNode;
  /**
   * The concept's delivery platforms, already narrowed to the placement matrix.
   * Absent means the `+` menu has nothing to offer and does not render.
   */
  platforms?: readonly { platform: MatrixPlatform; placement?: MatrixPlacement }[];

  /**
   * The Concept whose copy the `Content` section reads and writes. The section
   * owns its own data (`useConceptCopyVariants`) rather than taking flattened
   * strings: the copy layer needs the variant IDENTITY to anchor "More like
   * this" on, and a `string[]` of fields cannot carry one.
   */
  conceptId: string | null | undefined;
  /** The brief this concept belongs to — the ad-copy review path needs it. */
  briefId?: string;
  /**
   * The Content section's selected copy option, lifted here so
   * `ConceptDesignBody` can hand the SAME id to `ConceptPreviewPane` (W-1: the
   * preview used to always show Option 1 regardless of what was picked here).
   * Forwarded straight through to `ConceptCopySection` — see that component's
   * own doc comment for the controlled/uncontrolled contract.
   */
  selectedCopyVariantId?: string | null;
  onSelectCopyVariantId?: (id: string) => void;
  /**
   * The active brand kit's logo, per variant — resolves the `Variant` row on a
   * selected logo layer to the URL that variant actually points at. `null`
   * fields mean that variant has no dedicated asset; the row falls back to
   * `primary` rather than writing a dead URL onto the layer.
   */
  brandLogoUrls?: { primary: string | null; light: string | null; dark: string | null };
  /**
   * The concept's own editable fields (persona, angle, delivery, format,
   * proof), supplied by the route body which owns the draft and the
   * version-checked save. Passed as a slot so this component stays
   * presentational rather than growing a second data source.
   */
  conceptSection?: React.ReactNode;
  /**
   * The Critique section's target (selected size, its image, the overlay text) and the
   * existing controls its findings link to. Absent: the section does not render.
   */
  critique?: ConceptCritiqueSectionProps;

  layer: SetLayer | null;
  geometry: VariantLayerGeometry | null;
  variant: VariantState | null;
  onLayerChange: (patch: LayerOverride) => void;
  onGeometryChange: (patch: Partial<VariantLayerGeometry>) => void;
  className?: string;
}

export function WorkbenchInspector({
  variants,
  selectedRatio,
  onSelectRatio,
  onAddRatio,
  sizeActions,
  platforms,
  conceptId,
  briefId,
  selectedCopyVariantId,
  onSelectCopyVariantId,
  brandLogoUrls,
  conceptSection,
  critique,
  layer,
  geometry,
  variant,
  onLayerChange,
  onGeometryChange,
  className,
}: WorkbenchInspectorProps): JSX.Element {
  const textLayer = layer?.kind === 'text' ? layer : null;
  const shapeLayer: SetShapeLayer | null = layer?.kind === 'shape' ? layer : null;
  const logoLayer: SetLogoLayer | null = layer?.kind === 'logo' ? layer : null;

  /**
   * RATIO LOCK — the fix for a live silent no-op. `onToggle` was `() => {}`, so
   * the link button rendered, took focus, flipped nothing and constrained
   * nothing; `NumericPairRow` has always implemented the constraint and simply
   * never received a `true`.
   *
   * The lock is PER LAYER, not per panel: a logo whose aspect must hold and a
   * scrim that should stretch are different answers, and one shared boolean
   * would carry the logo's answer onto the scrim the moment the user clicked
   * between them. A logo defaults to locked (distorting a brand mark is almost
   * never intended) and can still be unlocked deliberately — a lock that cannot
   * be opened is the same class of lie as a toggle that does nothing.
   */
  const [ratioLockByLayer, setRatioLockByLayer] = useState<Record<string, boolean>>({});
  const ratioLocked = layer ? (ratioLockByLayer[layer.id] ?? layer.kind === 'logo') : false;

  const fontOptions = useMemo(
    () => OVERLAY_FONT_FAMILIES.map((family) => ({ value: family, label: family })),
    [],
  );

  const styleState: TextStyleState = {
    bold: (textLayer?.fontWeight ?? 400) >= 700,
    italic: textLayer?.isItalic ?? false,
    underline: textLayer?.isUnderline ?? false,
    allCaps: textLayer?.textTransform === 'uppercase',
  };

  const fontSize = textLayer ? materialiseFontSize(textLayer.fontScale, selectedRatio) : 0;

  return (
    <div className={cn('flex min-h-0 flex-col overflow-y-auto pb-12', className)}>
      {/* The strip owns its own `⇔ Size variants +` header. Wrapping it in a
          second SECTION_LABEL printed the title twice — caught on the live route,
          invisible to tsc and to every unit test. */}
      {/* NO PADDING ON THE WRAPPER. It carried `p-3` while the strip carries its
          own `px-2.5 py-2.5`, so the two paddings stacked: the `Size variants`
          label sat 22px from the top of the rail and 22px in from its edge,
          while `Layers` opposite sat at 20px and every accordion title below sat
          at 10px. 22 is also off the 4px grid. Removing the wrapper's padding
          leaves the strip's own 10px inset, which is the value every other
          section in this column already uses. */}
      <section className="border-b border-border/40">
        {/* `platforms` is what MAKES the `+` reachable: the strip renders the
            trigger only when the placement matrix offers a ratio this concept
            does not already have. Omitting it left `onAddRatio` wired to a
            button that never rendered — a silent no-op, invisible to tsc, to
            lint and to every unit test, and found by looking for the `+`. */}
        <RatioVariantStrip
          variants={variants}
          selected={selectedRatio}
          onSelect={onSelectRatio}
          platforms={platforms}
          onAdd={onAddRatio}
          actions={sizeActions}
        />
      </section>

      {/* THE SELECTED LAYER'S PROPERTIES COME FIRST — directly under the ratio
          strip, above the concept/copy governance sections below. See the
          file header comment for why this moved 2026-08-15. */}
      {/* COMPACT WHEN EMPTY. This placeholder used to sit at the BOTTOM of the
          rail, where its height cost nothing. Now that it holds the top slot it
          is read before Concept and Content on every open where no layer is
          selected — which is most opens — so a tall centred block here reads as
          a gap in the rail rather than as guidance. One quiet line, aligned to
          the rail's own rows. */}
      {!layer ? (
        <div className="border-b border-border/40 px-6 py-3">
          <p className="text-detail text-muted-foreground">
            Select a layer to style it, or use the prompt bar to change the image.
          </p>
        </div>
      ) : (
        <WorkbenchAccordion
          defaultOpen={['text', 'typography', 'appearance', 'position', 'dimensions']}
          className="border-b border-border/40 py-1"
        >
          {textLayer ? (
            <CollapsibleSection
              value="text"
              title="Text"
              icon={<TextAlignLeftIcon className="size-3.5" weight="regular" />}
            >
              <Textarea
                value={textLayer.text}
                rows={2}
                className="min-h-12 rounded-sm border-border/40 text-caption"
                onChange={(event) => onLayerChange({ text: event.target.value })}
              />
            </CollapsibleSection>
          ) : null}

          {textLayer ? (
            <CollapsibleSection value="typography" title="Typography">
              <div className="flex flex-col gap-1.5">
                <PropertyRow
                  variant="dropdown"
                  label="Font"
                  value={textLayer.fontFamily}
                  options={fontOptions}
                  optionStyle={(option) => ({ fontFamily: option.value })}
                  onChange={(value) => onLayerChange({ fontFamily: value })}
                />
                <PropertyRow
                  variant="dropdown"
                  label="Weight"
                  value={String(textLayer.fontWeight)}
                  options={FONT_WEIGHTS}
                  onChange={(value) => onLayerChange({ fontWeight: Number(value) })}
                />
                <PropertyRow
                  variant="input"
                  label="Size"
                  value={String(fontSize)}
                  unit="px"
                  inputMode="numeric"
                  onChange={() => {}}
                  onCommit={(value) => {
                    const next = Number(value);
                    if (!Number.isFinite(next) || next < 8 || next > 400) return;
                    // Against THIS ratio's stage width, never a hardcoded 1080.
                    // 16:9 authors at 1920, so a fixed denominator would silently
                    // halve every landscape headline.
                    onLayerChange({ fontScale: fontScaleFromSize(next, selectedRatio) });
                  }}
                />
                <FillRows fill={textLayer.fill} onChange={(fill) => onLayerChange({ fill })} />
                <PropertyRow
                  variant="dropdown"
                  label="Align"
                  value={textLayer.textAlign}
                  options={ALIGN_OPTIONS}
                  onChange={(value) =>
                    onLayerChange({ textAlign: value as SetTextLayer['textAlign'] })
                  }
                />
                <PropertyRow
                  variant="dropdown"
                  label="Vertical align"
                  value={textLayer.verticalAlign}
                  options={VERTICAL_ALIGN_OPTIONS}
                  onChange={(value) =>
                    onLayerChange({ verticalAlign: value as SetTextLayer['verticalAlign'] })
                  }
                />
                <TextStyleToggles
                  value={styleState}
                  onChange={(next) =>
                    onLayerChange({
                      fontWeight: next.bold ? 700 : 400,
                      isItalic: next.italic,
                      isUnderline: next.underline,
                      textTransform: next.allCaps ? 'uppercase' : 'none',
                    })
                  }
                />
              </div>
            </CollapsibleSection>
          ) : null}

          {/* SHAPE. Until now a shape got Position and Dimensions and nothing
              else — every property that decides what it LOOKS like was
              invisible, on the one layer kind whose whole job is to look like
              something. Fields, ranges and conditionality are read off
              `SetShapeProps` and off what `KonvaShapeNode` actually consumes:
              opacity and feather are 0–100 there (`element.opacity / 100`),
              NOT 0–1, which is also how the seeded default of `0.4` had been
              rendering a scrim at 0.4% opacity — invisible. */}
          {shapeLayer ? (
            <CollapsibleSection value="appearance" title="Appearance">
              <div className="flex flex-col gap-1.5">
                <PropertyRow
                  variant="dropdown"
                  label="Shape"
                  value={shapeLayer.shapeType}
                  options={SHAPE_TYPES}
                  onChange={(value) =>
                    onLayerChange({ shapeType: value as SetShapeLayer['shapeType'] })
                  }
                />
                <FillRows fill={shapeLayer.fill} onChange={(fill) => onLayerChange({ fill })} />
                <PropertyRow
                  variant="slider"
                  label="Opacity"
                  unit="%"
                  value={shapeLayer.opacity}
                  onChange={(value) => onLayerChange({ opacity: value })}
                />
                <PropertyRow
                  variant="slider"
                  label="Feather"
                  unit="%"
                  value={shapeLayer.feather}
                  onChange={(value) => onLayerChange({ feather: value })}
                />
                {shapeLayer.shapeType === 'rectangle' ? (
                  <PropertyRow
                    variant="slider"
                    label="Corner"
                    unit="%"
                    value={shapeLayer.cornerRadius ?? 0}
                    onChange={(value) => onLayerChange({ cornerRadius: value })}
                  />
                ) : null}
                {shapeLayer.shapeType === 'star' ? (
                  <>
                    <PropertyRow
                      variant="slider"
                      label="Points"
                      min={3}
                      max={12}
                      value={shapeLayer.points ?? 5}
                      onChange={(value) => onLayerChange({ points: value })}
                    />
                    <PropertyRow
                      variant="slider"
                      label="Inner"
                      unit="%"
                      value={shapeLayer.innerRadius ?? 50}
                      onChange={(value) => onLayerChange({ innerRadius: value })}
                    />
                  </>
                ) : null}
                {/* Stroke is offered on every shape, not just `line`, because
                    `KonvaShapeNode` honours it on every shape. It stays a plain
                    width + colour pair: a zero width IS "no stroke", so a
                    separate on/off toggle would be a second control for a state
                    the first one already expresses. */}
                <PropertyRow
                  variant="slider"
                  label="Stroke"
                  unit="px"
                  max={24}
                  value={shapeLayer.strokeWidth ?? 0}
                  onChange={(value) => onLayerChange({ strokeWidth: value })}
                />
                {(shapeLayer.strokeWidth ?? 0) > 0 ? (
                  <PropertyRow
                    variant="color"
                    label="Stroke colour"
                    value={shapeLayer.strokeColor ?? '#000000'}
                    onChange={(hex) => onLayerChange({ strokeColor: hex })}
                  />
                ) : null}
              </div>
            </CollapsibleSection>
          ) : null}

          {/* LOGO. Two real properties, and the opacity one has a different
              scale from the shape's: `KonvaLogoNode` passes `element.opacity`
              straight to Konva, so it is 0–1 there and 0–100 on a shape. The row
              shows a percentage in both places and converts here rather than
              exposing the inconsistency to the user. */}
          {logoLayer ? (
            <CollapsibleSection value="appearance" title="Appearance">
              <div className="flex flex-col gap-1.5">
                <PropertyRow
                  variant="dropdown"
                  label="Variant"
                  value={logoLayer.logoVariant}
                  options={LOGO_VARIANTS}
                  onChange={(value) => {
                    const nextVariant = value as SetLogoLayer['logoVariant'];
                    // A variant with no dedicated asset (most kits only have a
                    // primary mark) falls back to primary rather than writing a
                    // `null` URL onto the layer — the dropdown must always
                    // resolve to something that actually renders.
                    const nextUrl =
                      brandLogoUrls?.[nextVariant] ?? brandLogoUrls?.primary ?? logoLayer.logoUrl;
                    onLayerChange({ logoVariant: nextVariant, logoUrl: nextUrl });
                  }}
                />
                <PropertyRow
                  variant="slider"
                  label="Opacity"
                  unit="%"
                  value={Math.round(logoLayer.opacity * 100)}
                  onChange={(value) => onLayerChange({ opacity: value / 100 })}
                />
              </div>
            </CollapsibleSection>
          ) : null}

          <CollapsibleSection value="position" title="Position">
            <NumericPairRow
              fields={[
                { label: 'X', value: Math.round(geometry?.x ?? 0), unit: 'px', step: 1 },
                { label: 'Y', value: Math.round(geometry?.y ?? 0), unit: 'px', step: 1 },
              ]}
              onChange={([x, y]) => onGeometryChange({ x, y })}
              disabled={!geometry}
            />
          </CollapsibleSection>

          <CollapsibleSection value="dimensions" title="Dimensions">
            <NumericPairRow
              fields={[
                {
                  label: 'W',
                  value: Math.round(geometry?.width ?? 0),
                  unit: 'px',
                  min: 10,
                  step: 1,
                },
                {
                  label: 'H',
                  value: Math.round(geometry?.height ?? 0),
                  unit: 'px',
                  min: 10,
                  step: 1,
                },
              ]}
              onChange={([width, height]) => onGeometryChange({ width, height })}
              ratioLock={{
                locked: ratioLocked,
                onToggle: (next) =>
                  setRatioLockByLayer((current) => ({ ...current, [layer.id]: next })),
              }}
              disabled={!geometry}
            />
            {/* Rotation is per-ratio geometry like X/Y/W/H, and until now it was
                drag-only: there was no way to type 0 and get a headline square
                again after a stray transform handle. */}
            <PropertyRow
              variant="input"
              label="Rotation"
              unit="°"
              inputMode="decimal"
              disabled={!geometry}
              value={String(Math.round(geometry?.rotation ?? 0))}
              onChange={() => {}}
              onCommit={(value) => {
                const next = Number(value);
                if (!Number.isFinite(next)) return;
                onGeometryChange({ rotation: ((next % 360) + 360) % 360 });
              }}
            />
            {variant?.needsReview ? (
              <p className="pt-2 text-detail text-warning">
                This size still needs review after the last change.
              </p>
            ) : null}
          </CollapsibleSection>
        </WorkbenchAccordion>
      )}

      {conceptSection}

      <ConceptCopySection
        conceptId={conceptId}
        briefId={briefId}
        selectedId={selectedCopyVariantId}
        onSelectIdChange={onSelectCopyVariantId}
      />

      {critique ? <ConceptCritiqueSection {...critique} /> : null}
    </div>
  );
}
